import { Suspense, lazy } from "react";
import { Route, Switch } from "react-router-dom";

import Loading from "components/shared-components/Loading";
import { useCan } from "utils/hooks/useCan";
import Forbidden from "../auth-views/errors/forbidden";

const RouteAuth = ({ path, permissions = [], component: Component, ...rest }) => {

  const hasAbility = useCan({ permissions })

  return (hasAbility) ? (
    <Route {...rest} path={path} component={Component} />
  ) : (
    <Forbidden />
  );
};


export const FranchiseAppViews = () => {

  const groups = [

    {
      group: 'Home',
      description: 'Pagina inicial do admin',
      routes: [
        {
          permissions: ["dashboard.index"],
          description: 'Dashboard da Aplicação',
          path: "/dashboard",
          component: lazy(() => import("./Franchise/pages/home")),
        },
      ],
    },

    {
      group: 'Gestão de notas fiscais',
      description: 'Gestão de notas fiscais da plataforma',
      routes: [
        {
          permissions: ['invoices.products.index'],
          description: 'Listagem de faturas de produtos',
          path: '/invoices/products/index',
          component: lazy(() => import('./Franchise/pages/Invoices/ProductInvoice')),
        },
        {
          permissions: ['invoices.services.index'],
          description: 'Listagem de faturas de serviços',
          path: '/invoices/services/index',
          component: lazy(() => import('./Franchise/pages/Invoices/ServiceInvoice')),
        },
        {
          permissions: ['invoices.store'],
          description: 'Criar nova fatura',
          path: '/invoices/store',
          component: lazy(() => import('./Franchise/pages/Invoices/Create')),
        },
        {
            permissions: ['invoices.services.index'],
            description: "Configurar Dados da Empresa",
            path: "/invoices/config/my-data",
            component: lazy(() => import("./Franchise/pages/Invoices/Config/MyData")),
        },
        {
            permissions: ['invoices.services.index'],
            description: "Configurar Certificado",
            path: "/invoices/config/certificate",
            component: lazy(() => import("./Franchise/pages/Invoices/Config/Certificate")),
        }
      ]
    },

    {
      group: 'Gestão do Site',
      description: 'Gestão do site da franquia',
      routes: [
        {
          permissions: ['homepage.index'],
          description: 'Edição da pagina inicial',
          path: '/mywebsite/home-page',
          component: lazy(() => import('./Franchise/pages/Mysite/Homepage')),
        },
        {
          permissions: ['posts.index'],
          description: 'Listagem de posts',
          path: '/posts/index',
          component: lazy(() => import('./FranchiseeAdminGlobal/pages/Blog/')),
        },
        {
          permissions: ['posts.store'],
          description: 'Criar novo post',
          path: '/posts/store',
          component: lazy(() => import('./FranchiseeAdminGlobal/pages/Blog/Create')),
        },
        {
          permissions: ['post-categories.index'],
          description: 'Listagem de categorias de posts',
          path: '/post-categories/index',
          component: lazy(() => import('./FranchiseeAdminGlobal/pages/Blog/Categories/')),
        },
        {
          permissions: ['post-categories.store'],
          description: 'Criar nova categoria de post',
          path: '/post-categories/store',
          component: lazy(() => import('./FranchiseeAdminGlobal/pages/Blog/Categories/Create')),
        },
      ]
    },

    {
      group: 'Marketing',
      description: 'Funcionalidades de Marketing',
      routes: [
        //Follow Ups
        {
          path: `/follow-ups/index`,
          component: lazy(() => import(`./Franchise/pages/FollowUps/`)),
        },
        {
          path: `/follow-ups/store`,
          component: lazy(() => import(`./Franchise/pages/FollowUps/Create`)),
        },
        //Interessados
        {
          path: `/interesteds/index`,
          component: lazy(() => import(`./Franchise/pages/Interested/`)),
        },
        {
          path: `/interesteds/store`,
          component: lazy(() => import(`./Franchise/pages/Interested/Create`)),
        },
        //Campanhas
        {
          path: `/campaigns/index`,
          component: lazy(() => import(`./Franchise/pages/Campaign/`)),
        },
        {
          path: `/campaigns/store`,
          component: lazy(() => import(`./Franchise/pages/Campaign/Create`)),
        },
        //Calendário
        {
          permissions: [],
          description: 'Calendário Letivo',
          path: '/school-calendar/index',
          component: lazy(() => import(`./Franchise/pages/SchoolCalendar`)),
        },
        {
          permissions: ['calendar-events.index'],
          description: 'Listagem de eventos no calendário',
          path: '/calendar/index',
          component: lazy(() => import('./Franchise/pages/Calendar')),
        },
        {
          permissions: ['calendar-events.store'],
          description: 'Criar evento no calendário',
          path: '/calendar/store',
          component: lazy(() => import('./Franchise/pages/Calendar/Create')),
        },
      ]
    },

    {
      group: 'Espaço Físico',
      description: 'Gerenciamento de espaços fisicos da franquia',
      routes: [
        {
          permissions: [],
          description: 'Listagem de prédios',
          path: '/buildings/index',
          component: lazy(() => import('./Franchise/pages/PhysicalSpace/Buildings/')),
        },
        {
          permissions: [],
          description: 'Criar novo prédio',
          path: '/buildings/store',
          component: lazy(() => import('./Franchise/pages/PhysicalSpace/Buildings/Create')),
        },
        {
          permissions: [],
          description: 'Listagem de salas',
          path: '/rooms/index',
          component: lazy(() => import('./Franchise/pages/PhysicalSpace/Rooms/')),
        },
        {
          permissions: [],
          description: 'Criar nova sala',
          path: '/rooms/store',
          component: lazy(() => import('./Franchise/pages/PhysicalSpace/Rooms/Create')),
        },
        {
          permissions: [],
          description: 'Listagem de pavimentos',
          path: '/building-floors/index',
          component: lazy(() => import('./Franchise/pages/PhysicalSpace/Paviment/')),
        },
        {
          permissions: [],
          description: 'Criar novo pavimento',
          path: '/building-floors/store',
          component: lazy(() => import('./Franchise/pages/PhysicalSpace/Paviment/Create')),
        },
      ]
    },

    {
      group: 'Cursos e horários',
      description: 'Cursos e gestão de aulas',
      routes: [
        {
          permissions: [],
          description: 'Listagem de cursos',
          path: '/courses/index',
          component: lazy(() => import('./Franchise/pages/Course/')),
        },
        {
          permissions: ['team-schedules.index'],
          description: 'Listagem da grade horária',
          path: '/team-schedule',
          component: lazy(() => import('./Franchise/pages/TeamSchedules/')),
        },
        {
          permissions: [],
          description: 'Listagem de horários',
          path: '/team-schedules/index',
          component: lazy(() => import('./Franchise/pages/team-schedules/')),
        },
        {
          permissions: [],
          description: 'Criar novo horário',
          path: '/team-schedules/store',
          component: lazy(() => import('./Franchise/pages/team-schedules/Create/')),
        },
        {
          permissions: [],
          description: 'Listagem de avaliações',
          path: '/evaluation/index',
          component: lazy(() => import('./Franchise/pages/Evaluation/')),
        },
        {
          permissions: [],
          description: 'Listagem de métodos de avaliação',
          path: '/evaluation-methods/index',
          component: lazy(() => import('./Franchise/pages/Evaluation/EvaluationMethods')),
        },
        {
          permissions: [],
          description: 'Listagem de tipos de avaliação',
          path: '/evaluation-types/index',
          component: lazy(() => import('./Franchise/pages/Evaluation/EvaluationType')),
        },
        {
          permissions: [],
          description: 'Listagem de notas de avaliação',
          path: '/evaluation-grades/index',
          component: lazy(() => import('./Franchise/pages/Evaluation/EvaluationNotes')),
        },
        {
          permissions: [],
          description: 'Criar nota de avaliação',
          path: '/evaluation-grades/store',
          component: lazy(() => import('./Franchise/pages/Evaluation/EvaluationNotes/Create/')),
        },
      ]
    },

    {
      group: 'Products',
      description: 'Produtos oferecidos pela franqueadora',
      routes: [
        {
          permissions: ['products.index'],
          description: 'Listagem de produtos',
          path: "/products/index",
          component: lazy(() => import("./Franchise/pages/Product")),
        },
        {
          permissions: [],
          description: 'Listagem de categorias de produtos',
          path: "/product/category/index",
          component: lazy(() => import("./Franchise/pages/Product/Category")),
        },
      ]
    },

    {
      group: 'Gestão de Perfil',
      description: 'Gerenciamento de perfis de usuário',
      routes: [
        {
          permissions: [],
          description: 'Listagem de perfis',
          path: '/profile-management/index',
          component: lazy(() => import('./FranchiseeAdminGlobal/pages/Profile')),
        },
      ],
    },
    {
      group: 'Cargos',
      description: 'Gerenciamento de cargos',
      routes: [
        {
          permissions: [],
          description: 'Listagem de cargos',
          path: '/roles/index',
          component: lazy(() => import('./FranchiseeAdminGlobal/pages/Role')),
        },
        {
          permissions: [],
          description: 'Criar cargo',
          path: '/roles/store',
          component: lazy(() =>
            import('./FranchiseeAdminGlobal/pages/Role/Create')
          ),
        },
      ],
    },

    {
      group: 'Professores',
      description: 'Gerenciamento de professores',
      routes: [
        {
          permissions: [],
          description: 'Listagem de professores',
          path: '/teachers/index',
          component: lazy(() => import('./Franchise/pages/Teacher/')),
        },
        {
          permissions: [],
          description: 'Criar professor',
          path: '/teachers/store',
          component: lazy(() => import('./Franchise/pages/Teacher/Create')),
        },
      ],
    },
    {
      group: 'Usuários',
      description: 'Gerenciamento de usuários',
      routes: [
        {
          permissions: [],
          description: 'Listagem de usuários',
          path: '/users/index',
          component: lazy(() => import('./Franchise/pages/Users/')),
        },
        {
          permissions: [],
          description: 'Criar usuário',
          path: '/users/store',
          component: lazy(() => import('./Franchise/pages/Users/Create')),
        },
      ],
    },
    {
      group: 'Alunos',
      description: 'Gerenciamento de alunos',
      routes: [
        {
          permissions: ['students.index'],
          description: 'Listagem de alunos',
          path: '/students/index',
          component: lazy(() => import('./Franchise/pages/Users/Students/index')),
        },
        // ... outras rotas dentro do grupo 'Alunos' (se houver)
      ],
    },

    {
      group: 'Matrículas',
      description: 'Gerenciamento de matrículas',
      routes: [
        {
          permissions: ['enrollments.index'],
          description: 'Listagem de matrículas',
          path: '/enrollments/index',
          component: lazy(() => import('./Franchise/pages/Student/')),
        },
        {
          permissions: ['enrollments.index'],
          description: 'Criar matrícula',
          path: '/enrollments/store',
          component: lazy(() => import('./Franchise/pages/Student/Create')),
        },
        {
          permissions: ['enrollments.renovation'],
          description: 'Renovação de matrícula',
          path: '/enrollments/renovation',
          component: lazy(() => import('./Franchise/pages/Student/Renovation')),
        },
        {
          permissions: ['enrollments.index'],
          description: 'Matrícula de desenvolvedor',
          path: '/enrollments/dev/',
          component: lazy(() => import('./Franchise/pages/Enrollment/Create/')),
        },
      ],
    },

    {
      group: 'Turmas',
      description: 'Gerenciamento de turmas',
      routes: [
        {
          permissions: [],
          description: 'Criar turma',
          path: '/teams/store',
          component: lazy(() => import('./Franchise/pages/Class/Create')),
        },
        {
          permissions: [],
          description: 'Listagem de turmas',
          path: '/teams/index',
          component: lazy(() => import('./Franchise/pages/Class/')),
        },
      ],
    },

    {
      group: 'Perfil de Acesso',
      description: 'Gerenciamento de perfil de acesso',
      routes: [
        {
          permissions: [],
          description: 'Listagem de perfis de acesso',
          path: '/access-profile/index',
          component: lazy(() => import('./Franchise/pages/AccessProfile/')),
        },
        {
          permissions: [],
          description: 'Criar perfil de acesso',
          path: '/access-profile/store',
          component: lazy(() =>
            import('./Franchise/pages/AccessProfile/Create')
          ),
        },
      ],
    },

    {
      group: 'Gestão Financeira',
      description: 'Gerenciamento financeiro da franqueadora',
      routes: [
        {
          permissions: [],
          description: 'Painel de cobranças',
          path: '/franchise-charges/index',
          component: lazy(() => import('./Franchise/pages/FranchiseCharges/')),
        },
        {
          permissions: [],
          description: 'Fluxo de caixa',
          path: '/cash-flow/index',
          component: lazy(() => import('./Franchise/pages/CashFlow/')),
        },
        {
          permissions: [],
          description: 'Fornecedores',
          path: '/providers/index',
          component: lazy(() => import('./Franchise/pages/Providers/')),
        },
        {
          permissions: [],
          description: 'Cadastrar Fornecedor',
          path: '/providers/store',
          component: lazy(() => import('./Franchise/pages/Providers/Create/')),
        },
        {
          permissions: [],
          description: 'Contas a Pagar',
          path: '/bills-to-pay',
          component: lazy(() => import('./Franchise/pages/BillsToPay')),
        },
        {
          permissions: [],
          description: 'Contas a Receber',
          path: '/bills-to-receive',
          component: lazy(() => import('./Franchise/pages/BillsToReceive')),
        },
        {
          permissions: [],
          description: 'Contas a Pagar a Fazer',
          path: '/payment-to-do/index',
          component: lazy(() => import('./Franchise/pages/PaymentToDo/')),
        },
        {
          permissions: [],
          description: 'Cadastrar Conta a Pagar a Fazer',
          path: '/payment-to-do/store',
          component: lazy(() => import('./Franchise/pages/PaymentToDo/Create')),
        },
        {
          permissions: [],
          description: 'Contas Corrente',
          path: '/current_accounts/index',
          component: lazy(() => import('./Franchise/pages/CurrentAccounts/')),
        },
        {
          permissions: [],
          description: 'Minha Conta',
          path: '/myaccount',
          component: lazy(() => import('./Franchise/pages/MyAccount')),
        },
        {
          permissions: [],
          description: 'Desconto e Promoções',
          path: '/discount-management/index',
          component: lazy(() => import('./Franchise/pages/DiscountManagement/index')),
        },
        {
          permissions: [],
          description: 'Bolsas de Estudo',
          path: '/discount-management/scholarship/index',
          component: lazy(() => import('./Franchise/pages/DiscountManagement/Scholarship/index')),
        },
        {
          permissions: [],
          description: 'Cadastrar Bolsa de Estudo',
          path: '/discount-management/scholarship/store',
          component: lazy(() => import('./Franchise/pages/DiscountManagement/Scholarship/Create/index')),
        },
        {
          permissions: [],
          description: 'Migração',
          path: '/bills-receive/migration',
          component: lazy(() => import('./Franchise/pages/Migration/')),
        },
        {
          permissions: [],
          description: 'Registros',
          path: '/bills-receive/registers',
          component: lazy(() => import('./Franchise/pages/Migration/Registers')),
        },
        {
          permissions: [],
          description: 'Configurações',
          path: '/configuration',
          component: lazy(() => import('./Franchise/pages/Config')),
        },
        {
          permissions: [],
          description: 'Categorias de Contas',
          path: '/bill-categories/',
          component: lazy(() => import('./Franchise/pages/BillCategories/Categories')),
        },

      ],
    },
    {
      group: 'Suporte',
      description: 'Gerenciamento de suporte',
      routes: [
        // {
        //   permissions: [],
        //   description: 'Listagem de tickets',
        //   path: '/tickets/index',
        //   component: lazy(() => import('./Franchise/pages/Tickets/')),
        // },
        // {
        //   permissions: [],
        //   description: 'Criar ticket',
        //   path: '/tickets/store',
        //   component: lazy(() => import('./Franchise/pages/Tickets/Create')),
        // },
        {
          permissions: [],
          description: 'Listagem de chamados',
          path: '/suport/calls/index',
          component: lazy(() => import('./Franchise/pages/Suport/Calls/')),
        },
        {
          permissions: [],
          description: 'Visualização de chamados',
          path: '/suport/calls/:id/view',
          component: lazy(() => import('./Franchise/pages/Suport/Calls/View/')),
        },
        {
          permissions: [],
          description: 'Departamentos',
          path: '/suport/service-call-sectors/index',
          component: lazy(() => import('./Franchise/pages/Suport/Sectors')),
        },
        {
          permissions: [],
          description: 'Criar chamado',
          path: '/suport/calls/store',
          component: lazy(() => import('./Franchise/pages/Suport/Calls/Create')),
        },
        {
          permissions: [],
          description: 'Listagem de áreas',
          path: '/suport/areas/index',
          component: lazy(() => import('./Franchise/pages/Suport/Areas/')),
        }

      ],
    },
    {
      group: 'Relatórios',
      description: 'Relatórios Gerais',
      routes: [
        {
          permissions: [],
          description: 'Relatório de material',
          path: `/reports/sales/didactic-material`,
          component: lazy(() => import(`./FranchiseeAdminGlobal/pages/Reports/Marketing/material`)),
        },
        {
          path: `/reports/academic/teams`,
          component: lazy(() => import(`./Franchise/pages/Reports/Academic/teams`)),
        },
        {
          path: `/reports/academic/analytical-teams`,
          component: lazy(() => import(`./Franchise/pages/Reports/Academic/analytical-teams`)),
        },
        {
          path: `/reports/academic/scholarships`,
          component: lazy(() => import(`./Franchise/pages/Reports/Academic/scholarships`)),
        },
        {
          path: `/reports/academic/frequency`,
          component: lazy(() => import(`./Franchise/pages/Reports/Academic/frequency`)),
        },
        {
          path: `/reports/academic/frequency-by-team`,
          component: lazy(() => import(`./Franchise/pages/Reports/Academic/frequency_by_team`)),
        },
        {
          path: `/reports/academic/enrollment-analytics`,
          component: lazy(() => import(`./Franchise/pages/Reports/Academic/enrollment-analytics`)),
        },
        {
          path: `/reports/academic/synthetical-enrollments`,
          component: lazy(() => import(`./Franchise/pages/Reports/Academic/synthetical-enrollments`)),
        },
        {
          path: `/reports/academic/evaluation-analytics`,
          component: lazy(() => import(`./Franchise/pages/Reports/Academic/evaluation-analytics`)),
        },
        {
          path: `/reports/academic/grades`,
          component: lazy(() => import(`./Franchise/pages/Reports/Academic/grades`)),
        },
        {
          path: `/reports/sales/index`,
          component: lazy(() => import(`./Franchise/pages/Reports/Sales/`)),
        },
        {
          path: `/reports/sales/sales-analytics`,
          component: lazy(() => import(`./Franchise/pages/Reports/Sales/sales-analytics`)),
        },
        {
          path: `/reports/financial/financial-statement`,
          component: lazy(() => import(`./Franchise/pages/Reports/Financial/financial-statement`)),
        },
        {
          path: `/reports/financial/expected-payments`,
          component: lazy(() => import(`./Franchise/pages/Reports/Financial/expected-payments`)),
        },
        {
          path: `/reports/financial/invoice-statement`,
          component: lazy(() => import(`./Franchise/pages/Reports/Financial/invoice-statement`)),
        },
        {
          path: `/reports/financial/withdraw-conciliation`,
          component: lazy(() => import(`./Franchise/pages/Reports/Financial/withdraw-conciliation`)),
        },
        {
          path: `/reports/financial/analytical-incomes`,
          component: lazy(() => import(`./Franchise/pages/Reports/Financial/analytical-incomes`)),
        },
        {
          path: `/reports/financial/synthetical-incomes`,
          component: lazy(() => import(`./Franchise/pages/Reports/Financial/synthetical-incomes`)),
        },
        {
          path: `/reports/financial/settled`,
          component: lazy(() => import(`./FranchiseeAdminGlobal/pages/Reports/Financial/settled`)),
        },
        {
          path: `/reports/financial/revenue-manager`,
          component: lazy(() => import(`./Franchise/pages/Reports/Financial/revenue-manager`)),
        },
        {
          path: `/reports/financial/defaulter`,
          component: lazy(() => import(`./Franchise/pages/Reports/Financial/defaulter`)),
        },
        {
          path: `/reports/financial/bills-receive`,
          component: lazy(() => import(`./Franchise/pages/Reports/Financial/bills-receive`)),
        },
        {
          path: `/reports/financial/forecast`,
          component: lazy(() => import(`./Franchise/pages/Reports/Financial/forecast`)),
        },
        {
          path: `/reports/access/leads`,
          component: lazy(() => import(`./Franchise/pages/Reports/Access/leads`)),
        },
        {
          path: `/reports/access/access-environment`,
          component: lazy(() => import(`./Franchise/pages/Reports/Access/access-environment`)),
        },
        {
          path: `/reports/access/manage-access`,
          component: lazy(() => import(`./Franchise/pages/Reports/Access/manage-access`)),
        },
        {
          path: `/reports/marketing/follow-up`,
          component: lazy(() => import(`./Franchise/pages/Reports/Marketing/follow-up`)),
        },
        {
          path: `/reports/marketing/newsletter`,
          component: lazy(() => import(`./Franchise/pages/Reports/Marketing/newsletter`)),
        },
        {
          path: `/reports/marketing/consultants`,
          component: lazy(() => import(`./Franchise/pages/Reports/Marketing/consultants`)),
        },
        {
          path: `/reports/marketing/birthdays`,
          component: lazy(() => import(`./Franchise/pages/Reports/Marketing/birthdays`)),
        },
        {
          path: `/reports/financial/consolidated-receivables`,
          component: lazy(() => import(`./Franchise/pages/Reports/Financial/consolidated-receivables`)),
        },
        {
          path: `/reports/financial/consolidated-statement`,
          component: lazy(() => import(`./Franchise/pages/Reports/Financial/consolidated-statement`)),
        },
        {
          path: `/reports/financial/financial-balance`,
          component: lazy(() => import(`./Franchise/pages/Reports/Financial/balance`)),
        },
        {
          path: `/reports/financial/cash-flow`,
          component: lazy(() => import(`./Franchise/pages/Reports/Financial/cash-flow`)),
        },
        {
          path: `/reports/financial/negotiated-bills`,
          component: lazy(() => import(`./Franchise/pages/Reports/Financial/negotiated-bills`)),
        },
        {
          path: `/reports/financial/franchisor-royalties`,
          component: lazy(() => import(`./Franchise/pages/Reports/Financial/franchisor-royalties`)),
        }
      ]
    },


  ]

  const allRoutes = groups.flatMap((group) => group.routes);


  return (
    <Suspense fallback={<Loading cover="content" />}>
      <Switch>
        {allRoutes.map(({ component, path }) => <RouteAuth path={path} component={component} />)}
      </Switch>
    </Suspense>
  );
};
